import ExcelJS from 'exceljs';
import { CargoEventReportData } from '../../ReportGeneratorTypes';
import { setCellValueWithFont, setupWorksheetForPrint, setWorksheetPrintArea } from './reportEventExcelUtils';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import {
    addChartererAndDateTime,
    addOdfjellHeader,
    addVesselDetails,
    getCargoName,
    getCargoOperationForEvent,
    odfjellHeaderColor,
    replaceLoadDischargePhrase,
} from './parts/helpers';
import { TankersEvent } from 'src/types/ViewModel/TankersEvent';

const addReportHeader = (worksheet: ExcelJS.Worksheet, logoImageId: number) => {
    let rowNum = addOdfjellHeader(worksheet, logoImageId);
    rowNum = 6;
    setCellValueWithFont(worksheet, rowNum, 1, `PROTEST OF DIFFERENCE`, {
        bold: true,
        color: odfjellHeaderColor,
        fontSize: 16,
        mergeAcross: 10,
    });
    rowNum++;
    setCellValueWithFont(worksheet, rowNum, 1, `BETWEEN SHIP & SHORE FIGURES`, {
        bold: true,
        color: odfjellHeaderColor,
        fontSize: 16,
        mergeAcross: 10,
    });

    return rowNum + 1;
};

const addCargoDescription = (worksheet: ExcelJS.Worksheet, cargoOperation: RotationCargoOperation, rowNum: number) => {
    rowNum++;
    setCellValueWithFont(worksheet, rowNum, 1, 'Please be advised that there is a discrepancy between ship and shore figures with regards to', {
        mergeAcross: 9,
        align: 'left',
    });
    rowNum++;
    setCellValueWithFont(
        worksheet,
        rowNum,
        1,
        `${getCargoName(cargoOperation)} ${replaceLoadDischargePhrase(cargoOperation, false, 'ing')} at ${
            cargoOperation.berthCall.berthName ?? 'Unknown'
        }`,
        {
            mergeAcross: 9,
            mergeDown: 2,
            align: 'left',
            verticalAlign: 'top',
        }
    );
    return rowNum + 1;
};

const addDiffFigures = (worksheet: ExcelJS.Worksheet, event: TankersEvent, rowNum: number): number => {
    const shore = event.eventTypeInputRegList.find((o) => o.eventTypeInputId == 47)?.decimalValueReg;
    const ship = event.eventTypeInputRegList.find((o) => o.eventTypeInputId == 48)?.decimalValueReg;

    if (shore === undefined || ship === undefined || shore === null || ship === null) return rowNum;

    rowNum += 3;

    const lines = [
        { label: 'Shore Figures :', value: shore },
        { label: 'Ship Figures :', value: ship },
        { label: 'Difference :', value: shore - ship },
    ];

    lines.forEach((l, idx) => {
        const isLast = idx === lines.length - 1;
        const bottomBorder = { border: { bottom: { style: isLast ? 'double' : 'thin' } } } as const;
        setCellValueWithFont(worksheet, rowNum, 3, l.label, { bold: true, align: 'left', mergeAcross: 3 });
        setCellValueWithFont(worksheet, rowNum, 6, l.value, { align: 'right', ...bottomBorder });
        setCellValueWithFont(worksheet, rowNum, 7, 'Mt', { align: 'left', ...bottomBorder });
        rowNum++;
    });

    rowNum += 2;

    setCellValueWithFont(
        worksheet,
        rowNum,
        1,
        'We hereby lodge a protest accordingly, and we, including my disponent Owners, hold You and/or Charterers responsible for any cost, delays and consequences.',
        { align: 'left', mergeAcross: 9, mergeDown: 2, verticalAlign: 'top', wrapText: true }
    );

    return rowNum++;
};

const setDefaultColumnWidths = (workheet: ExcelJS.Worksheet) => {
    workheet.getColumn(1).width = 9;
    workheet.getColumn(2).width = 2;
    workheet.getColumn(3).width = 14;
    workheet.getColumn(4).width = 21;
    workheet.getColumn(5).width = 9;
    workheet.getColumn(6).width = 17;
    workheet.getColumn(7).width = 10;
    workheet.getColumn(8).width = 14;
    workheet.getColumn(9).width = 7;
};

export const generateProtestDiffReport = (workbook: ExcelJS.Workbook, cargoEventReportData: CargoEventReportData, logoImageId: number): void => {
    const _worksheet = workbook.addWorksheet('Diff');
    setupWorksheetForPrint(_worksheet);
    // Hide gridlines and keep a clean white background
    _worksheet.views = [{ state: 'normal', showGridLines: false }];
    const portCall = cargoEventReportData.selectedPortCall;
    const selectedEvent = cargoEventReportData.tankersEvents.find((ev) => !ev.deleted && ev.eventId === cargoEventReportData.excelReportInfo.eventId);
    const cargoOperation = getCargoOperationForEvent(
        portCall.berthCalls.flatMap((berthCall) => berthCall.cargoOperations),
        selectedEvent
    );
    if (!cargoOperation || !selectedEvent) return;
    setDefaultColumnWidths(_worksheet);
    addReportHeader(_worksheet, logoImageId);
    let rowNum = addVesselDetails(
        _worksheet,
        9,
        1,
        7,
        portCall.voyageNumber,
        portCall.portDisplayName,
        cargoEventReportData.vessel,
        selectedEvent.cargoNumber,
        selectedEvent.eventDate.actualDateTimeLocal
    );
    rowNum = addCargoDescription(_worksheet, cargoOperation, rowNum + 3);
    rowNum = addDiffFigures(_worksheet, selectedEvent, rowNum);
    const endRow = addChartererAndDateTime(_worksheet, rowNum + 8, cargoOperation, selectedEvent, 'Master/Agent');
    setWorksheetPrintArea(_worksheet, endRow, 9);
};
